import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Users, CheckCircle, Download, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Logo } from '../components/Logo';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventAdminPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/admin/event-stats`);
        setStats(res.data);
      } catch (err) {
        console.error('Failed to load stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
    const interval = setInterval(fetchStats, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleExport = () => {
    window.open(`${API}/admin/event-export`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = [
    { label: 'Sessions Started', value: stats?.sessions_started || 0, icon: Users, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { label: 'Briefings Generated', value: stats?.briefings_generated || 0, icon: BarChart3, color: 'text-purple-500', bg: 'bg-purple-500/10' },
    { label: 'Sessions Completed', value: stats?.sessions_completed || 0, icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
    { label: 'Completion Rate', value: `${stats?.completion_rate || 0}%`, icon: TrendingUp, color: 'text-amber-500', bg: 'bg-amber-500/10' },
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-background p-6">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Logo className="h-8" />
            <div>
              <h1 className="text-xl font-bold">Event Dashboard</h1>
              <p className="text-xs text-muted-foreground">Live session tracking</p>
            </div>
          </div>
          <Button variant="outline" onClick={handleExport} className="rounded-full gap-2" data-testid="export-csv-btn">
            <Download size={16} /> Export CSV
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-8">
          {statCards.map((s) => {
            const Icon = s.icon;
            return (
              <Card key={s.label} className="p-4 bg-card" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center`}>
                    <Icon size={18} className={s.color} />
                  </div>
                </div>
                <p className="text-2xl font-bold">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </Card>
            );
          })}
        </div>

        {stats?.source_breakdown && Object.keys(stats.source_breakdown).length > 0 && (
          <Card className="p-5 mb-6 bg-card">
            <h3 className="font-medium text-sm mb-3">Source Breakdown</h3>
            <div className="space-y-2">
              {Object.entries(stats.source_breakdown).map(([source, count]) => (
                <div key={source} className="flex items-center justify-between">
                  <Badge variant="secondary">{source}</Badge>
                  <span className="font-medium text-sm">{count}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {stats?.event_breakdown && Object.keys(stats.event_breakdown).length > 0 && (
          <Card className="p-5 bg-card">
            <h3 className="font-medium text-sm mb-3">Event Breakdown</h3>
            <div className="space-y-2">
              {Object.entries(stats.event_breakdown).map(([event, count]) => (
                <div key={event} className="flex items-center justify-between">
                  <Badge variant="secondary">{event}</Badge>
                  <span className="font-medium text-sm">{count}</span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </motion.div>
  );
}
